import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import LayoutHeader from '../../layout/LayoutHeader';
import { getCachedData, refreshData } from '../../utils/dataPreloader';

const Works = () => {
    const navigate = useNavigate();
    const cachedCatalog = useSelector((state) => state.dataCache?.catalog?.data);
    const [catalog, setCatalog] = useState(cachedCatalog || []);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);

    useEffect(() => {
        // Use cached data immediately if available
        if (cachedCatalog) {
            setCatalog(cachedCatalog);
        }
        
        // Refresh in background if needed
            const token = JSON.parse(localStorage.getItem('dizeeToken'));
        if (token) {
            refreshData('catalog', 'user/getCatalog', token).then((data) => {
                if (data && data.length > 0) {
                    setCatalog(data);
                }
            });
            }
    }, [cachedCatalog]);

    const filteredCatalog = catalog.filter(song => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            song.song_title?.toLowerCase().includes(query) ||
            song.primary_artist?.toLowerCase().includes(query) ||
            song.album_title?.toLowerCase().includes(query) ||
            song.genre?.toLowerCase().includes(query)
        );
    });

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatDuration = (duration) => {
        if (!duration) return '';
        if (typeof duration === 'string' && duration.includes(':')) {
            return duration;
        }
        return duration;
    };

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0] overflow-x-hidden">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Works</span>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setShowSearch(!showSearch)}
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
                            >
                                {showSearch ? 'Cancel' : 'Search'}
                            </button>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/catalog/view-all')}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    {/* Search Interface */}
                    {showSearch && (
                        <div className="w-full px-4 mb-6">
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <svg className="w-4 h-4 text-[#17A2B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Search Works</span>
                                </div>
                                
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Enter song title, artist, album..."
                                        className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#17A2B8]"
                                    />
                                    
                                    {searchQuery && (
                                        <button
                                            onClick={() => setSearchQuery('')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto overflow-x-hidden box-border">
                        {filteredCatalog.length === 0 ? (
                            <div className="text-left py-[60px]">
                                <h3 className="text-white text-[12px] font-normal mb-2">
                                    {searchQuery ? `No results for "${searchQuery}"` : 'No works found'}
                                </h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                                    {searchQuery 
                                        ? 'Try different search terms or check your spelling.'
                                        : 'Add songs to your catalog to see them here.'
                                    }
                                </p>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4 w-full max-w-full">
                                {filteredCatalog.map((song, index) => (
                                    <div 
                                        key={song._id || index}
                                        onClick={() => navigate(`/catalog/works/${song._id}`)}
                                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#17A2B8] hover:bg-[rgba(23,162,184,0.05)] transition-all cursor-pointer w-full max-w-full box-border"
                                    >
                                        <div className="flex items-start gap-4 min-w-0 w-full">
                                            {/* Album Art */}
                                            <div className="w-12 h-12 bg-[rgba(23,162,184,0.2)] rounded-lg flex items-center justify-center flex-shrink-0">
                                                {song.album_artwork ? (
                                                    <img 
                                                        src={song.album_artwork} 
                                                        alt={song.song_title || 'Album art'} 
                                                        className="w-full h-full object-cover rounded-lg"
                                                    />
                                                ) : (
                                                    <svg className="w-6 h-6 text-[#17A2B8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Song Info */}
                                            <div className="flex-1 min-w-0 overflow-hidden">
                                                <h3 className="text-white text-[12px] font-normal mb-1 break-words">
                                                    {song.song_title || 'Untitled'}
                                                </h3>
                                                <p className="text-[rgba(255,255,255,0.7)] text-[11px] mb-1 break-words">
                                                    {song.primary_artist || 'Unknown Artist'}
                                                </p>
                                                <div className="flex items-center gap-2 text-[rgba(255,255,255,0.5)] text-[10px] flex-wrap">
                                                    {song.release_date && (
                                                        <span className="whitespace-nowrap">{formatDate(song.release_date)}</span>
                                                    )}
                                                    {song.duration && (
                                                        <>
                                                            {song.release_date && <span>•</span>}
                                                            <span className="whitespace-nowrap">{formatDuration(song.duration)}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Arrow */}
                                            <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)] flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default Works;

